import React from 'react';
import { Home } from 'lucide-react';
import api from '../../utils/axios';
import CTable from '../shared/c-table';
import Pagination from '../Pagination';
import StatusBadge from '../StatusBadge';
import { useAuth } from '../../contexts/AuthContext';
import { wfhStatus } from './ApprovalFilters';
import {
  FALLBACK_TZ,
  QueueSection,
  RowActions,
  actionButtonClass,
  dayColumn,
  employeeColumn,
  useApprovalQueue,
} from './approvalQueue';
import { formatTime } from './daySections';

const DESCRIPTION = 'Days timed in from home, and office time-ins recorded offline that the app could not '
  + "verify. Approving or rejecting records your decision on the employee's timesheet.";

// The server caps the rejection reason at 200 characters.
const REASON_LIMIT = 200;

const isOfficeConfirmation = (row) => row.approval_kind === 'office_confirmation';

const distanceText = (metres) => {
  if (metres >= 1000) return `${(metres / 1000).toFixed(1)} km`;
  return `${metres} m`;
};

const wifiText = (seen) => {
  if (seen === true) return 'office Wi-Fi seen';
  if (seen === false) return 'office Wi-Fi not seen';
  return 'no Wi-Fi reading';
};

// What the phone reported, so the manager can judge an office time-in the app could not verify.
const evidenceLine = (evidence) => {
  const parts = [];
  if (evidence.distance_m == null) {
    parts.push('No location');
  } else {
    parts.push(`${distanceText(evidence.distance_m)} from the office`);
  }
  parts.push(wifiText(evidence.office_wifi));
  if (evidence.mocked) parts.push('location not verifiable on this phone');
  return parts.join(', ');
};

/**
 * Manager/HR queue of from-home punches and office confirmations, each waiting for
 * an approve or reject decision. The server refuses a decision on your own punch.
 */
const WfhQueue = ({ filters }) => {
  const { hasPermission } = useAuth();
  const canDecide = hasPermission('timesheet', 'acknowledge');

  const queue = useApprovalQueue({
    endpoint: '/api/timesheet/wfh',
    failureMessage: 'Failed to load from-home requests',
    filters,
    buildParams: ({ status, departmentId, debouncedSearch }) => ({
      status: wfhStatus(status),
      department_id: departmentId || undefined,
      q: debouncedSearch || undefined,
    }),
  });

  const { confirm, setConfirm } = queue;
  const rejecting = confirm?.decision === 'rejected';
  const reason = confirm?.reason || '';
  const confirmingOffice = !!confirm?.office;

  let savedMessage = `From-home request ${confirm?.decision}`;
  let prompt = rejecting ? 'Reject this from-home day?' : 'Approve this from-home day?';
  if (confirmingOffice) {
    savedMessage = rejecting ? 'Office time-in rejected' : 'Office time-in confirmed';
    prompt = rejecting ? 'Reject this office time-in?' : 'Confirm this office time-in?';
  }

  const decide = () => queue.save(
    () => api.put(
      `/api/timesheet/wfh/${confirm.id}`,
      rejecting ? { decision: 'rejected', reason: reason.trim() } : { decision: confirm.decision }
    ),
    savedMessage
  );

  // The employee sees this on their timesheet, so it is required rather than a
  // nicety. Lives on the confirm state, which clears itself when the row closes.
  const reasonField = rejecting && (
    <div className="mt-1 w-64">
      <label htmlFor="wfh-reject-reason" className="block text-xs font-medium text-gray-700">
        Reason <span className="text-danger-600">*</span>
      </label>
      <textarea
        id="wfh-reject-reason"
        data-testid="wfh-reject-reason"
        rows={2}
        maxLength={REASON_LIMIT}
        value={reason}
        onChange={(e) => setConfirm({ ...confirm, reason: e.target.value })}
        placeholder="Required - the employee sees this"
        className={'mt-1 w-full resize-none rounded-md border border-gray-300 px-3 py-2 text-sm'
          + ' focus:border-primary-500 focus:outline-none focus:ring-primary-500'}
      />
      <p className="text-right text-xs text-gray-400">{reason.length}/{REASON_LIMIT}</p>
    </div>
  );

  const columns = [
    employeeColumn,
    dayColumn,
    {
      key: 'request',
      header: 'Request',
      cellClassName: 'whitespace-normal',
      render: (row) => {
        if (!isOfficeConfirmation(row)) return 'From home';
        return (
          <>
            Office confirmation
            {row.evidence && (
              <span className="block text-xs text-gray-500" data-testid={`wfh-${row.id}-evidence`}>
                {evidenceLine(row.evidence)}
              </span>
            )}
          </>
        );
      },
    },
    {
      key: 'at',
      header: 'Time',
      cellClassName: 'text-gray-500',
      render: (row) => formatTime(row.at, row.timezone || FALLBACK_TZ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusBadge status={row.wfh_approval} type="leave" data-testid={`wfh-${row.id}-status`} />,
    },
    {
      key: 'decided_by',
      header: 'Decided by',
      cellClassName: 'text-gray-500',
      render: (row) => {
        if (!row.wfh_decided_by) return '-';
        return row.wfh_decided_by_name || `User #${row.wfh_decided_by}`;
      },
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cellClassName: 'whitespace-normal',
      render: (row) => (
        <RowActions
          queue={queue}
          prefix="wfh"
          row={row}
          prompt={prompt}
          fields={reasonField}
          confirmDisabled={rejecting && !reason.trim()}
          onConfirm={decide}
        >
          {canDecide && row.wfh_approval === 'pending' && (
            <>
              <button
                type="button"
                onClick={() => setConfirm({ id: row.id, decision: 'approved', office: isOfficeConfirmation(row) })}
                data-testid={`wfh-${row.id}-approve`}
                className={`${actionButtonClass} border-success-600 text-success-700 hover:bg-success-50`}
              >
                {isOfficeConfirmation(row) ? 'Confirm' : 'Approve'}
              </button>
              <button
                type="button"
                onClick={() => setConfirm({ id: row.id, decision: 'rejected', office: isOfficeConfirmation(row) })}
                data-testid={`wfh-${row.id}-reject`}
                className={`${actionButtonClass} border-danger-600 text-danger-700 hover:bg-danger-50`}
              >
                Reject
              </button>
            </>
          )}
        </RowActions>
      ),
    },
  ];

  return (
    <QueueSection
      queue={queue}
      testId="wfh"
      title="From home and office"
      count={queue.total}
      description={DESCRIPTION}
    >
      <CTable
        columns={columns}
        data={queue.rows}
        loading={queue.loading}
        rowKey="id"
        emptyMessage={
          <div className="text-gray-400">
            <Home className="mx-auto h-12 w-12 mb-3" />
            <p className="text-sm">
              {filters.status === 'pending'
                ? 'Nothing from home or the office waiting for a decision'
                : 'No from-home requests or office confirmations found'}
            </p>
          </div>
        }
        getRowProps={(row) => ({ 'data-testid': `wfh-row-${row.id}` })}
      />
      <Pagination page={queue.page} totalPages={queue.totalPages} onPageChange={queue.setPage} />
    </QueueSection>
  );
};

export default WfhQueue;
